import React, { useState, useEffect } from 'react';
import { estimateService } from '../services/estimateService';

const EMPTY_ITEM = { description: '', value: '', type: 'line' };
const EMPTY_EXPENSE = { description: '', value: '', type: 'expense' };

const EstimateModal = ({ jobId, estimate, defaultTitle = '', onClose, onSaved }) => {
  const [title, setTitle] = useState(defaultTitle);
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const isEdit = !!estimate;

  useEffect(() => {
    if (estimate) {
      setTitle(estimate.title || '');
      setItems(
        estimate.items && estimate.items.length > 0
          ? estimate.items.map(i => {
              const v = parseFloat(i.value);
              const type = v < 0 ? 'expense' : 'line';
              return { description: i.description, value: String(Math.abs(v)), type };
            })
          : [{ ...EMPTY_ITEM }]
      );
    } else {
      setTitle(defaultTitle);
    }
  }, [estimate]);

  const handleItemChange = (index, field, value) => {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const addItem = () => {
    setItems(prev => [...prev, { ...EMPTY_ITEM }]);
  };

  const addExpense = () => {
    setItems(prev => [...prev, { ...EMPTY_EXPENSE }]);
  };

  const removeItem = (index) => {
    if (items.length === 1) return;
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const calcTotal = () => {
    return items.reduce((sum, item) => {
      const v = parseFloat(item.value);
      if (isNaN(v)) return sum;
      return sum + (item.type === 'expense' ? -Math.abs(v) : Math.abs(v));
    }, 0);
  };

  const calcQuoteTotal = () => {
    return items.reduce((sum, item) => {
      const v = parseFloat(item.value);
      if (isNaN(v)) return sum;
      return sum + Math.abs(v);
    }, 0);
  };

  const formatValue = (value) => {
    return `£${parseFloat(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const validate = () => {
    const errs = {};
    if (!title.trim()) errs.title = 'Title is required.';
    items.forEach((item, i) => {
      if (!item.description.trim()) errs[`item_desc_${i}`] = 'Description required.';
      if (item.value === '' || isNaN(parseFloat(item.value))) errs[`item_val_${i}`] = 'Valid value required.';
    });
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setSubmitting(true);
    setErrors({});
    try {
      const payload = {
        job_id: jobId,
        title: title.trim(),
        items: items.map((item, i) => {
          const abs = Math.abs(parseFloat(item.value));
          return {
            description: item.description.trim(),
            value: item.type === 'expense' ? -abs : abs,
            sort_order: i,
          };
        }),
      };
      let saved;
      if (isEdit) {
        saved = await estimateService.updateEstimate(estimate.id, payload);
      } else {
        saved = await estimateService.createEstimate(payload);
      }
      onSaved(saved.data);
    } catch (err) {
      const apiErrors = err.response?.data?.errors;
      if (apiErrors) {
        setErrors(apiErrors);
      } else {
        setErrors({ general: 'An error occurred. Please try again.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const total = calcTotal();
  const quoteTotal = calcQuoteTotal();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isEdit ? 'Edit Estimate' : 'Add Estimate'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
            {errors.general && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
                {errors.general}
              </div>
            )}

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => { setTitle(e.target.value); setErrors(p => ({ ...p, title: undefined })); }}
                placeholder="e.g. Initial Estimate"
                className={`w-full px-3 py-2 rounded-lg border ${errors.title ? 'border-red-400 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm`}
              />
              {errors.title && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.title}</p>}
            </div>

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Line Items</label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={addItem}
                    className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition-colors"
                  >
                    + Add line
                  </button>
                  <button
                    type="button"
                    onClick={addExpense}
                    className="text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 transition-colors"
                  >
                    + Add expense
                  </button>
                </div>
              </div>

              {/* Column headers */}
              <div className="grid grid-cols-[80px_1fr_130px_32px] gap-2 mb-1 px-1">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</span>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Description</span>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">Value (£)</span>
                <span />
              </div>

              <div className="space-y-2">
                {items.map((item, index) => (
                  <div key={index} className="grid grid-cols-[80px_1fr_130px_32px] gap-2 items-start">
                    {/* Type badge */}
                    <div className="pt-2">
                      {item.type === 'expense' ? (
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">Expense</span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">Line</span>
                      )}
                    </div>
                    <div>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => { handleItemChange(index, 'description', e.target.value); setErrors(p => ({ ...p, [`item_desc_${index}`]: undefined })); }}
                        placeholder="Description"
                        className={`w-full px-3 py-2 rounded-lg border ${errors[`item_desc_${index}`] ? 'border-red-400 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm`}
                      />
                      {errors[`item_desc_${index}`] && <p className="mt-0.5 text-xs text-red-600 dark:text-red-400">{errors[`item_desc_${index}`]}</p>}
                    </div>
                    <div>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.value}
                        onChange={(e) => { handleItemChange(index, 'value', e.target.value); setErrors(p => ({ ...p, [`item_val_${index}`]: undefined })); }}
                        placeholder="0.00"
                        className={`w-full px-3 py-2 rounded-lg border ${errors[`item_val_${index}`] ? 'border-red-400 dark:border-red-500' : item.type === 'expense' ? 'border-red-200 dark:border-red-800' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-700 ${item.type === 'expense' ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'} placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                      />
                      {errors[`item_val_${index}`] && <p className="mt-0.5 text-xs text-red-600 dark:text-red-400">{errors[`item_val_${index}`]}</p>}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      disabled={items.length === 1}
                      className="mt-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="pt-2 border-t border-gray-200 dark:border-gray-700 space-y-2">
              <div className="flex justify-end">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
                    {formatValue(quoteTotal)}
                  </span>
                </div>
              </div>
              <div className="flex justify-end">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Gross Profit</span>
                  <span className={`text-lg font-bold ${total < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                    {formatValue(total)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Estimate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EstimateModal;
